import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrendingDown, TrendingUp, Users, DollarSign, Calendar, PieChart, Building2, ChevronDown, AlertCircle, Check } from 'lucide-react';
import financeService from '../services/financeService';
import costService from '../services/costService';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

// Helper functions for date filters
const getMonthName = (month) => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[month - 1] || '';
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }
  return years;
};

const DateFilter = ({ filters, setFilters }) => {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const handlePreviousMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;

    if (newMonth === 1) {
      newMonth = 12;
      newYear -= 1;
    } else {
      newMonth -= 1;
    }
    setFilters({ month: newMonth.toString(), year: newYear.toString() });
  };

  const handleNextMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;

    if (newMonth === 12) {
      newMonth = 1;
      newYear += 1;
    } else {
      newMonth += 1;
    }
    setFilters({ month: newMonth.toString(), year: newYear.toString() });
  };

  const handleCurrentMonth = () => {
    setFilters({ month: currentMonth.toString(), year: currentYear.toString() });
  };

  const displayMonth = filters.month ? parseInt(filters.month) : currentMonth;
  const displayYear = filters.year ? parseInt(filters.year) : currentYear;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handlePreviousMonth}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
          title="Mês Anterior"
        >
          ‹
        </button>

        <select
          value={filters.month || currentMonth}
          onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          className="px-3 py-2 border rounded-lg bg-white text-gray-700 font-medium min-w-[130px]"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{getMonthName(m)}</option>
          ))}
        </select>

        <select
          value={filters.year || currentYear}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
          className="px-3 py-2 border rounded-lg bg-white text-gray-700 font-medium min-w-[100px]"
        >
          {getYearOptions().map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <button
          onClick={handleNextMonth}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
          title="Próximo Mês"
        >
          ›
        </button>

        <button
          onClick={handleCurrentMonth}
          className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg font-medium transition-colors text-sm"
        >
          Mês Atual
        </button>

        <span className="ml-auto text-sm text-gray-600 font-medium">
          Período: {getMonthName(displayMonth)} / {displayYear}
        </span>
      </div>
    </div>
  );
};

const FinanceForecastedExpenses = () => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, 'financial:view') || hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:expenses');

  const [filters, setFilters] = useState({ month: '', year: '' });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmActionId, setConfirmActionId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    payroll: true,
    futureCosts: true,
    recurringCosts: true,
    distribution: true
  });
  const { data, isLoading } = useQuery({
    queryKey: ['finance-forecasted-expenses', filters],
    queryFn: () => {
      const params = {};
      if (filters.month && filters.month !== '') params.month = filters.month;
      if (filters.year && filters.year !== '') params.year = filters.year;
      return financeService.getForecastedExpenses(params);
    },
    staleTime: 0, // Sempre considerar os dados como stale para refetch automático
    refetchOnWindowFocus: true
  });

  const forecastData = data?.data || {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    payroll: [],
    futureCosts: [],
    recurringPending: [],
    projectedRemaining: null,
    totals: {
      payroll: 0,
      futureCosts: 0,
      recurringPending: 0,
      projectedRemaining: 0,
      total: 0
    }
  };

  // Recurring pending from API (despesas_previstas)
  const recurringPending = forecastData.recurringPending || [];

  // Buscar usuários ativos para agrupar por departamento
  const { data: usersData } = useQuery({
    queryKey: ['users-for-forecast'],
    queryFn: () => userService.getUsers({ limit: 200, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    return [];
  }, [usersData]);

  // Agrupar folha de pagamento por departamento
  const payrollByDepartment = useMemo(() => {
    if (!forecastData.payroll || !users.length) return [];
    
    const departmentMap = {};
    
    forecastData.payroll.forEach(item => {
      const user = users.find(u => u.id === item.user_id);
      const department = user?.role || 'Sem Departamento';
      
      if (!departmentMap[department]) {
        departmentMap[department] = {
          department,
          employees: [],
          totalGross: 0,
          totalDeductions: 0,
          totalNet: 0
        };
      }
      
      departmentMap[department].employees.push(item);
      departmentMap[department].totalGross += item.gross_salary || 0;
      departmentMap[department].totalDeductions += item.advance_deduction || 0;
      departmentMap[department].totalNet += item.net_salary || 0;
    });
    
    return Object.values(departmentMap).sort((a, b) => 
      b.totalNet - a.totalNet
    );
  }, [forecastData.payroll, users]);

  // Mutation para confirmar pagamento
  const queryClient = useQueryClient();
  const confirmMutation = useMutation({
    mutationFn: (id) => costService.confirmRecurringCost(id),
    onSuccess: () => {
      toast.success('Pagamento confirmado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['finance-forecasted-expenses'] });
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao confirmar pagamento');
    }
  });

  const handleConfirmPayment = (id) => {
    setConfirmActionId(id);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmModalConfirm = () => {
    if (confirmActionId) {
      confirmMutation.mutate(confirmActionId);
    }
    setIsConfirmModalOpen(false);
    setConfirmActionId(null);
  };

  const handleConfirmModalCancel = () => {
    setIsConfirmModalOpen(false);
    setConfirmActionId(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Você não tem permissão para visualizar o módulo financeiro.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Saídas Previstas</h1>
          <p className="text-gray-500 mt-1">Visão detalhada das saídas previstas para o período</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/financeiro" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Dashboard</Link>
          <Link to="/financeiro/entradas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Entradas</Link>
          <Link to="/financeiro/saidas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Saídas</Link>
          <Link to="/financeiro/folha" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Folha</Link>
        </div>
      </div>

      {/* Filtros */}
      <DateFilter filters={filters} setFilters={setFilters} />

      {/* Cards de totais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="group relative bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-primary-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary-400 to-primary-600" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Folha de Pagamento</p>
              <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(forecastData.totals.payroll)}</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-xl group-hover:bg-primary-100 transition-colors">
              <Users className="w-6 h-6 text-primary-500" />
            </div>
          </div>
        </div>

        <div className="group relative bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-green-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-400 to-green-600" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Custos Futuros</p>
              <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(forecastData.totals.futureCosts)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
              <Calendar className="w-6 h-6 text-green-500" />
            </div>
          </div>
        </div>

        <div className="group relative bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-orange-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-400 to-orange-600" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Média Histórica</p>
              <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(forecastData.totals.projectedRemaining)}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
              <TrendingUp className="w-6 h-6 text-orange-500" />
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-primary-600 to-primary-700 border-2 border-primary-500 rounded-2xl p-5 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/30 to-white/10" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-primary-200 mb-1">Total Previsto</p>
              <p className="text-2xl font-bold text-white tracking-tight">{formatCurrency(forecastData.totals.total)}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Despesas Recorrentes Pendentes de Confirmação */}
      {recurringPending.length > 0 && (
        <div className="bg-white rounded-lg shadow border-l-4 border-red-500">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-red-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    Despesas Recorrentes Aguardando Confirmação
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {recurringPending.length} {recurringPending.length === 1 ? 'despesa pendente' : 'despesas pendentes'} de confirmação
                  </p>
                </div>
              </div>
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                Total: {formatCurrency(recurringPending.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0))}
              </span>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {recurringPending.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.description}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(() => {
                        const datePart = item.due_date.split('T')[0];
                        const [year, month, day] = datePart.split('-');
                        const dueDate = new Date(year, month - 1, day);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isPast = dueDate < today;
                        return (
                          <>
                            <span className={`font-medium ${isPast ? 'text-red-600' : 'text-green-600'}`}>
                              {isPast ? 'Venceu:' : 'Vence:'}
                            </span>
                            {' '}
                            {`${day}/${month}/${year}`}
                            <span className="mx-2">•</span>
                            <span className="font-medium">{item.category}</span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-red-600">
                      {formatCurrency(item.amount)}
                    </span>
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-medium">
                      ⏳ A Vencer
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Seção de Folha de Pagamento Prevista - Com Dropdown */}
      <div className="bg-white rounded-lg shadow border-l-4 border-blue-500">
        <div 
          className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100 cursor-pointer hover:from-blue-100 hover:to-blue-200 transition-colors"
          onClick={() => setExpandedSections(prev => ({ ...prev, payroll: !prev.payroll }))}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {expandedSections.payroll ? (
                <ChevronDown className="h-5 w-5 text-blue-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-blue-600 rotate-180" />
              )}
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Folha de Pagamento Prevista
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {forecastData.payroll.length} {forecastData.payroll.length === 1 ? 'funcionário' : 'funcionários'} • {payrollByDepartment.length} {payrollByDepartment.length === 1 ? 'departamento' : 'departamentos'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">Total Líquido</p>
              <p className="text-2xl font-bold text-blue-700">
                {formatCurrency(forecastData.totals.payroll)}
              </p>
            </div>
          </div>
        </div>
        {expandedSections.payroll && (
          <>
            {isLoading ? (
              <div className="p-8 text-center text-gray-600">Carregando...</div>
            ) : forecastData.payroll.length === 0 ? (
              <div className="p-8 text-center text-gray-500">Nenhum salário previsto encontrado</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {payrollByDepartment.map((dept, deptIdx) => (
                  <div key={dept.department} className="p-4 hover:bg-gray-50 transition-colors">
                    {/* Cabeçalho Compacto do Departamento */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-900">{dept.department}</h3>
                        <span className="text-xs text-gray-500">
                          ({dept.employees.length} {dept.employees.length === 1 ? 'funcionário' : 'funcionários'})
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-600">
                          Bruto: <span className="font-medium text-gray-900">{formatCurrency(dept.totalGross)}</span>
                        </span>
                        {dept.totalDeductions > 0 && (
                          <span className="text-red-600">
                            Deduções: <span className="font-medium">{formatCurrency(dept.totalDeductions)}</span>
                          </span>
                        )}
                        <span className="text-blue-600 font-semibold">
                          Líquido: {formatCurrency(dept.totalNet)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Lista Compacta de Funcionários */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {dept.employees.map((item, idx) => (
                        <div key={`${item.user_id}-${idx}`} className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-300 transition-colors">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{item.user_name}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                              item.source === 'payslip' 
                                ? 'bg-blue-100 text-blue-800' 
                                : item.source === 'vacation'
                                ? 'bg-purple-100 text-purple-800 font-semibold'
                                : item.source === '13th_first_half' || item.source === '13th_second_half'
                                ? 'bg-amber-100 text-amber-800 font-semibold'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {item.source === 'payslip' 
                                ? 'Holerite' 
                                : item.source === 'vacation' 
                                ? 'Férias' 
                                : item.source === '13th_first_half'
                                ? '13º (1ª Parc.)'
                                : item.source === '13th_second_half'
                                ? '13º (2ª Parc.)'
                                : 'Estimativa'}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Bruto:</span>
                              <span className="font-medium text-gray-900">{formatCurrency(item.gross_salary)}</span>
                            </div>
                            {item.advance_deduction > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Desconto:</span>
                                <span className="font-medium text-red-600">{formatCurrency(item.advance_deduction)}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-1 border-t border-gray-200">
                              <span className="text-gray-700 font-medium">Líquido:</span>
                              <span className="font-bold text-blue-600">{formatCurrency(item.net_salary)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Seção de Custos Futuros - Com Dropdown */}
      {forecastData.futureCosts && forecastData.futureCosts.length > 0 && (
        <div className="bg-white rounded-lg shadow border-l-4 border-green-500">
          <div
            className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-green-100 cursor-pointer hover:from-green-100 hover:to-green-200 transition-colors"
            onClick={() => setExpandedSections(prev => ({ ...prev, futureCosts: !prev.futureCosts }))}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {expandedSections.futureCosts ? (
                  <ChevronDown className="h-5 w-5 text-green-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-green-600 rotate-180" />
                )}
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-green-600" />
                    Custos Futuros
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {forecastData.futureCosts.length} {forecastData.futureCosts.length === 1 ? 'custo agendado' : 'custos agendados'} para datas futuras
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Total</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(forecastData.totals.futureCosts)}
                </p>
              </div>
            </div>
          </div>
          {expandedSections.futureCosts && (
            <div className="p-4">
              <div className="space-y-3">
                {forecastData.futureCosts.map((item) => (
                  <div key={item.id} className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{item.description}</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{item.category}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {item.expense_date ? item.expense_date.split('-').reverse().join('/') : '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Seção de Projeção - Média Histórica Dias Restantes */}
      {forecastData.projectedRemaining && forecastData.projectedRemaining.days_remaining > 0 && (
        <div className="bg-white rounded-lg shadow border-l-4 border-orange-500">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <TrendingUp className="h-6 w-6 text-orange-600" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    Projeção para {forecastData.projectedRemaining.days_remaining} dias restantes
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Baseado em média histórica de meses anteriores
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Média diária</p>
                <p className="text-lg font-bold text-orange-600">
                  {formatCurrency(forecastData.projectedRemaining.avg_daily)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Total projetado
                </p>
                <p className="text-xl font-bold text-orange-700">
                  {formatCurrency(forecastData.projectedRemaining.projected_amount)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de distribuição - Com Dropdown */}
      {forecastData.totals.total > 0 && (
        <div className="bg-white rounded-lg shadow border-l-4 border-purple-500">
          <div 
            className="p-6 border-b border-gray-200 cursor-pointer hover:bg-purple-50 transition-colors"
            onClick={() => setExpandedSections(prev => ({ ...prev, distribution: !prev.distribution }))}
          >
            <div className="flex items-center gap-3">
              {expandedSections.distribution ? (
                <ChevronDown className="h-5 w-5 text-purple-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-purple-600 rotate-180" />
              )}
              <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                <PieChart className="h-5 w-5 text-purple-600" />
                Distribuição das Saídas Previstas
              </h3>
            </div>
          </div>
          {expandedSections.distribution && (
            <div className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-32 h-32 drop-shadow-lg">
                {(() => {
                  const values = [
                    forecastData.totals.payroll,
                    forecastData.totals.futureCosts,
                    forecastData.totals.recurringPending,
                    forecastData.totals.projectedRemaining
                  ];
                  const total = values.reduce((a, b) => a + b, 0) || 1;
                  let acc = 0;
                  const colors = ['#3b82f6', '#22c55e', '#ef4444', '#f97316'];

                  return values.map((v, i) => {
                    if (v === 0) return null;
                    const start = (acc / total) * 2 * Math.PI - Math.PI / 2;
                    acc += v;
                    const end = (acc / total) * 2 * Math.PI - Math.PI / 2;
                    const large = end - start > Math.PI ? 1 : 0;
                    const r = 45, cx = 50, cy = 50;
                    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
                    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
                    const d = v === total
                      ? `M ${cx} ${cy} m -${r} 0 a ${r} ${r} 0 1 1 ${r * 2} 0 a ${r} ${r} 0 1 1 -${r * 2} 0`
                      : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
                    return <path key={i} d={d} fill={colors[i]} stroke="white" strokeWidth="1" />;
                  });
                })()}
              </svg>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded bg-blue-500"></span>
                  <span className="text-sm font-medium text-gray-700">Folha de Pagamento</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(forecastData.totals.payroll)}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({((forecastData.totals.payroll / forecastData.totals.total) * 100).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded bg-green-500"></span>
                  <span className="text-sm font-medium text-gray-700">Custos Futuros</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(forecastData.totals.futureCosts)}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({((forecastData.totals.futureCosts / forecastData.totals.total) * 100).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded bg-red-500"></span>
                  <span className="text-sm font-medium text-gray-700">Recorrentes Pendentes</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(forecastData.totals.recurringPending)}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({((forecastData.totals.recurringPending / forecastData.totals.total) * 100).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded bg-orange-500"></span>
                  <span className="text-sm font-medium text-gray-700">Média Histórica</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(forecastData.totals.projectedRemaining)}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({((forecastData.totals.projectedRemaining / forecastData.totals.total) * 100).toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        show={isConfirmModalOpen}
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleConfirmModalCancel}
        title="Confirmar Pagamento"
        message="Confirmar o pagamento desta despesa? O valor será debitado do Caixa da Empresa."
        confirmText="Confirmar"
        cancelText="Cancelar"
        type="warning"
        isLoading={confirmMutation.isPending}
      />
    </div>
  );
};

export default FinanceForecastedExpenses;

